import { fetchDataWithParams, postData } from "../../shared/services/service";
import { ChecklistItem, InitialChecks, OptionType, TaskItem } from "../models/delivery-plan.interfaces";
import { datadogRum } from "@datadog/browser-rum";
import { Dispatch, SetStateAction } from "react";

export function setBeOptions(filteredData: any, staticOptions: OptionType[]): OptionType[] {
  return staticOptions.map((staticOption) => {
    const matchingApiData = filteredData.find(
      (apiData: TaskItem) =>
        apiData.TASK_DESCRIPTION === staticOption.value && apiData.RECURRING_DISPLAY_DROPDOWN === "N",
    );

    if (matchingApiData) {
      return { ...staticOption, isDisabled: true };
    } else {
      return staticOption;
    }
  });
}

export async function addChecklistItem(
  tcCode: string | undefined,
  voyageNo: string | undefined,
  keyActivity: string,
  selectedOption: OptionType,
  fullName: string,
  email: string,
) {
  const requestData = {
    tc_code: tcCode,
    voyage_no: voyageNo,
    task_name: selectedOption.value,
    task_type: "Recurring",
    operator: fullName,
    is_checked: "N",
    key_activity: keyActivity,
    is_deleted: "N",
    is_added: "Y",
  };
  datadogRum.addAction("voyageCreateTask", {
    ext_user_email: email,
    ...requestData,
  });
  const filteredRequestData = Object.fromEntries(
    Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
  ) as Record<string, string>;
  const queryParams = new URLSearchParams(filteredRequestData).toString();
  const result = await postData(`modifytcvcchecklist?${queryParams}`, requestData);

  const output = result.data;
  return output;
}

export async function getChecklist(
  tcCode: string | undefined,
  voyageNo: string | undefined,
  keyActivity: string,
): Promise<{ filteredData: any; initialCheckedState: InitialChecks }> {
  const requestData = {
    tc_code: tcCode,
    voyage_no: voyageNo,
    key_activity: keyActivity,
  };

  const filteredRequestData = Object.fromEntries(
    Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
  ) as Record<string, string>;

  const queryParams = new URLSearchParams(filteredRequestData).toString();
  const result = await fetchDataWithParams(`getchecklist?${queryParams}`);

  const filteredData = result.data
    ?.filter((item: TaskItem) => !(!item.RECURRING_DISPLAY_DROPDOWN && item.TASK_TYPE === "Recurring"))
    .sort((a: TaskItem, b: TaskItem) => {
      // Sort by LAST_UPDATED_GMT, putting null or empty dates at the end
      const getDateValue = (item: TaskItem) => {
        const dateValue = item.LAST_UPDATED_GMT?.trim();
        return dateValue ? new Date(dateValue).getTime() : 0;
      };

      // Prioritize by LAST_UPDATED_GMT in descending order
      return getDateValue(b) - getDateValue(a);
    });

  const initialCheckedState: InitialChecks = {};

  filteredData.forEach((item: ChecklistItem) => {
    initialCheckedState[item.TASK_ID] = item.IS_CHECKED === "Y";
  });

  return { filteredData, initialCheckedState };
}

export const sendCheckboxStatus = async (
  item: TaskItem,
  tcCode: string | undefined,
  voyageNo: string | undefined,
  fullName: string,
  setLoading: Dispatch<SetStateAction<boolean>>,
  fetchData: () => Promise<void>,
  email: string,
) => {
  try {
    const requestData = {
      tc_code: tcCode,
      voyage_no: voyageNo,
      task_name: item.TASK_DESCRIPTION,
      task_type: item.TASK_TYPE,
      operator: fullName,
      is_checked: item.IS_CHECKED,
      key_activity: item.KEY_ACTIVITY,
      is_deleted: "N",
      is_added: "N",
    };
    datadogRum.addAction("voyageCheckedTask", {
      ext_user_email: email,
      ...requestData,
    });
    const filteredRequestData = Object.fromEntries(
      Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
    ) as Record<string, string>;
    const queryParams = new URLSearchParams(filteredRequestData).toString();
    setLoading(true);
    const result = await postData(`modifytcvcchecklist?${queryParams}`, requestData);

    const output = result.data;
    if (output.success === true) {
      fetchData();
      setLoading(false);
    }
  } catch (error: unknown) {
    setLoading(false);
  }
};

export async function addCustomTask(
  tcCode: string | undefined,
  voyageNo: string | undefined,
  keyActivity: string,
  inputText: string,
  fullName: string,
) {
  const requestData = {
    tc_code: tcCode,
    voyage_no: voyageNo,
    task_name: inputText,
    task_type: "Custom",
    operator: fullName,
    is_checked: "N",
    key_activity: keyActivity,
    is_deleted: "N",
    is_added: "Y",
  };
  const filteredRequestData = Object.fromEntries(
    Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
  ) as Record<string, string>;

  const queryParams = new URLSearchParams(filteredRequestData).toString();
  const result = await postData(`modifytcvcchecklist?${queryParams}`, requestData);

  return result.data;
}

export async function deleteOption(
  tcCode: string | undefined,
  voyageNo: string | undefined,
  item: TaskItem,
  fullName: string,
) {
  const requestData = {
    tc_code: tcCode,
    voyage_no: voyageNo,
    task_name: item.TASK_DESCRIPTION,
    task_type: item.TASK_TYPE,
    operator: fullName,
    is_checked: "N",
    key_activity: item.KEY_ACTIVITY,
    is_deleted: "Y",
    is_added: "N",
  };
  const filteredRequestData = Object.fromEntries(
    Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
  ) as Record<string, string>;
  const queryParams = new URLSearchParams(filteredRequestData).toString();
  const result = await postData(`modifytcvcchecklist?${queryParams}`, requestData);

  const output = result.data;
  return output;
}

export async function resetToPreset(
  tcCode: string | undefined,
  voyageNo: string | undefined,
  item: TaskItem,
  fullName: string,
) {
  const requestData = {
    tc_code: tcCode,
    voyage_no: voyageNo,
    task_name: item.TASK_DESCRIPTION,
    task_type: item.TASK_TYPE,
    operator: fullName,
    key_activity: item.KEY_ACTIVITY,
  };
  const filteredRequestData = Object.fromEntries(
    Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
  ) as Record<string, string>;
  const queryParams = new URLSearchParams(filteredRequestData).toString();
  const result = await postData(`resettopresenttcvcchecklist?${queryParams}`, requestData);

  const output = result.data;
  return output;
}

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const extractInitials = (fullName: string | null): { firstNameInitial: string; lastNameInitial: string } => {
  const defaultInitials = { firstNameInitial: "", lastNameInitial: "" };
  if (!fullName) return defaultInitials;

  const nameArray = fullName.split(" ");
  const firstNameInitial = nameArray[0]?.charAt(0) ?? "";
  const lastNameInitial = nameArray.length > 1 ? nameArray[1]?.charAt(0) ?? "" : "";

  return { firstNameInitial, lastNameInitial };
};
